import { animate, style, transition, trigger } from '@angular/animations';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnChanges, OnInit, SimpleChanges, input, model, output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { BehaviorSubject, Observable } from 'rxjs';

import { tap } from 'rxjs/operators';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';

import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  IndustryPack,
  IndustryPackService,
} from 'app/providers/semantria-api/industry-pack.service';
import {
  LanguageTemplate,
  LanguageTemplateService,
} from 'app/providers/semantria-api/language-template.service';
import { SemantriaUsersService } from 'app/providers/semantria-api/semantria-users.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';
import { AddConfigurationLayerDialogOptions } from '../add-configuration-layer-dialog/add-configuration-layer-dialog.component';
import { ConfigurationDetailsTabBase } from '../configuration-details-tab-base';
import { ConfigurationVersionTrackerService } from '../../services/configuration-version-tracker.service';

export type ConfigurationLayer = (IndustryPack | LanguageTemplate | SemantriaConfiguration) & {
  isIndustryPack?: boolean;
  isLanguageTemplate?: boolean;
  isConfiguration?: boolean;
};

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-layers',
  templateUrl: './configuration-layers.component.html',
  styleUrls: ['./configuration-layers.component.scss'],
  animations: [
    trigger('actionButtonsTrigger', [
      transition(':enter', [
        style({ height: '0' }),
        animate('0.3s ease-in-out', style({ height: '60px' })),
      ]),
      transition(':leave', [animate('0.3s ease-in-out', style({ height: '0' }))]),
    ]),
  ],
  standalone: false,
})
export class ConfigurationLayersComponent
  extends ConfigurationDetailsTabBase
  implements OnInit, OnChanges
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  readonly configuration = model<SemantriaConfiguration>();
  readonly configurationLimits = input<any>();
  readonly industryPacks = input<IndustryPack[]>([]);
  readonly languageTemplates = input<LanguageTemplate[]>([]);
  readonly configurationLayers = model<ConfigurationLayer[]>([]);

  readonly layerHasChanges = output<boolean>();

  session: Session;

  showAddLayerDialog;
  addLayerDialogData: AddConfigurationLayerDialogOptions;

  originalConfigurationParents: string[];
  originalConfigurationLayers: ConfigurationLayer[];
  selectedLayer;
  selectedLayerCreator;
  selectedLayerConfigurationItems = [
    {
      prop: 'sentiment',
      countProp: 'sentiment_phrases_count',
      label: translate('analytic-items.sentiment'),
      used: -1,
      total: 0,
    },
    {
      prop: 'entities',
      countProp: 'user_entities_count',
      label: translate('analytic-items.entities'),
      used: -1,
      total: 0,
    },
    {
      prop: 'queries',
      countProp: 'query_topic_count',
      label: translate('analytic-items.queries'),
      used: -1,
      total: 0,
    },
    {
      prop: 'taxonomy',
      countProp: 'taxonomy_count',
      label: translate('analytic-items.taxonomy'),
      used: -1,
      total: 0,
    },
    {
      prop: 'concept_topics',
      countProp: 'concept_topics_count',
      label: translate('analytic-items.concept-topics'),
      used: -1,
      total: 0,
    },
    {
      prop: 'blacklist',
      countProp: 'blacklist_count',
      label: translate('analytic-items.blacklist'),
      used: -1,
      total: 0,
    },
  ];

  configurations: SemantriaConfiguration[];

  canEditLayers;
  canEditLayersTooltip = translate('configuration-layers-component.read-only-no-layers');
  canDeleteLayers;

  hasChanges$ = new BehaviorSubject<boolean>(false);
  hasChangesObs: Observable<boolean>;

  isLoading;
  groupId: string;

  constructor(
    private configService: ConfigurationService,
    private languageTemplateService: LanguageTemplateService,
    private industryPackService: IndustryPackService,
    private authService: AuthService,
    private dialogService: DialogService,
    private userService: SemantriaUsersService,
    private groupService: GroupService,
    private alertService: AlertService,
    protected route: ActivatedRoute,
    protected configVersionTracker: ConfigurationVersionTrackerService,
    private router: Router
  ) {
    super(route, configVersionTracker);
  }

  async ngOnInit() {
    const url = this.route.snapshot;
    this.groupId = url.paramMap.get('groupId');
    this.hasChangesObs = this.hasChanges$
      .asObservable()
      .pipe(tap((val) => this.layerHasChanges.emit(val)));

    this.session = await this.authService.getSession();
    this.canEditLayers = !this.authService.isReadOnly;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.configurationLimits?.currentValue) {
      Object.keys(this.configurationLimits()).forEach((k) => {
        const item = this.selectedLayerConfigurationItems.find((ci) => ci.prop === k);
        if (item) {
          item.total = this.configurationLimits()[k].total;
        }
      });
    }

    if (changes.configuration?.currentValue) {
      this.originalConfigurationParents = [...this.configuration().parents];
      this.canDeleteLayers = this.configuration().parents.length > 1;
    }
    if (changes.configurationLayers?.currentValue) {
      this.originalConfigurationLayers = [...this.configurationLayers()];
    }
  }

  async onAddLayerClicked() {
    this.isLoading = true;

    if (!this.configurations) {
      this.configurations = await this.configService.getConfigurations(
        false,
        this.groupService.getSelectedGroupId()
      );
    }

    this.addLayerDialogData = {
      standardConfigs: {
        language: this.languageTemplates().filter(
          (lt) =>
            lt.language_id === this.configuration().language_id &&
            !this.configurationLayers().some((l) => l.id === lt.id)
        ),
        configuration: this.configurations.filter(
          (c) =>
            c.language_id === this.configuration().language_id &&
            !this.configurationLayers().some((l) => l.id === c.id) &&
            !c.layer
        ),
        pack: this.industryPacks().filter(
          (ip) =>
            ip.language_id === this.configuration().language_id &&
            !this.configurationLayers().some((l) => l.id === ip.id)
        ),
      },
      layeredConfigs: {
        language: [],
        configuration: this.configurations.filter(
          (c) =>
            c.language_id === this.configuration().language_id &&
            !this.configurationLayers().some((l) => l.id === c.id) &&
            this.configuration().id !== c.id &&
            c.layer
        ),
        pack: [],
      },
    };

    this.isLoading = false;
    this.showAddLayerDialog = true;
  }

  onAddLayerDialogClosed($event: AddConfigurationLayerDialogOptions) {
    if ($event) {
      const addLayerFactory = (prop) => {
        return (lt) => {
          this.configurationLayers.set([...this.configurationLayers(), { ...lt, [prop]: true }]);
          this.configuration.update((config) => {
            return { ...config, parents: [...config.parents, lt.id] };
          });
        };
      };

      $event.standardConfigs.language.forEach(addLayerFactory('isLanguageTemplate'));
      $event.standardConfigs.configuration.forEach(addLayerFactory('isConfiguration'));
      $event.standardConfigs.pack.forEach(addLayerFactory('isIndustryPack'));
      $event.layeredConfigs.configuration.forEach(addLayerFactory('isConfiguration'));

      this.fireChanges();
    }

    this.showAddLayerDialog = false;
  }

  onDeleteLayerClicked(index) {
    this.configurationLayers.set(this.configurationLayers().filter((_, i) => i !== index));
    this.configuration.update((config) => ({
      ...config,
      parents: config.parents.filter((_, i) => i !== index),
    }));
    this.canDeleteLayers = this.configuration().parents.length > 1;

    this.fireChanges();
  }

  selectLayer(layer) {
    this.selectedLayer = layer;
    this.selectedLayerCreator = '...';
    Object.values(this.selectedLayerConfigurationItems).forEach((v) => ((v.used as any) = '…'));

    if (this.selectedLayer.creator_user_id !== 'API') {
      this.userService.getUser(this.selectedLayer.creator_user_id).then((res) => {
        this.selectedLayerCreator = ((res.first_name || '') + ' ' + (res.last_name || '')).trim();
        if (!this.selectedLayerCreator) {
          this.selectedLayerCreator = res.email;
        }
      });
    } else {
      this.selectedLayerCreator = 'API';
    }

    let configItemsCountPromise;
    if (layer.isIndustryPack) {
      configItemsCountPromise = this.industryPackService.getConfigurationItemCounts(layer.id);
    } else if (layer.isLanguageTemplate) {
      configItemsCountPromise = this.languageTemplateService.getConfigurationItemCounts(layer.id);
    } else {
      configItemsCountPromise = this.configService.getConfigurationItemCounts(layer.id);
    }

    configItemsCountPromise.then((res) => {
      Object.keys(res).forEach((k) => {
        const item = this.selectedLayerConfigurationItems.find((ci) => ci.countProp === k);
        if (item) {
          item.used = res[k];
        }
      });
    });
  }

  onLayerReordered($event) {
    const layers = [...this.configurationLayers()];
    moveItemInArray(layers, $event.previousIndex, $event.currentIndex);
    this.configurationLayers.set(layers);

    this.configuration.update((config) => {
      const parents = [...config.parents];
      moveItemInArray(parents, $event.previousIndex, $event.currentIndex);
      return { ...config, parents };
    });

    this.fireChanges();
  }

  fireChanges() {
    this.hasChanges$.next(
      !Utils.deepEquals(this.originalConfigurationParents, this.configuration().parents)
    );
  }

  onEditConfigurationClicked() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['groups', this.groupId, 'configurations', this.selectedLayer.id])
    );

    window.open(url);
  }

  async checkQueryTopicEdit(resolve) {
    if (this.hasChanges$.value) {
      this.dialogService
        .showSimpleDialog({
          title: translate('configuration-layers-component.changes-title'),
          content: translate('configuration-layers-component.changes-content'),
          confirmTitle: translate('configuration-layers-component.changes-confirm'),
          confirmColor: SimpleDialogButtonColor.green,
          cancelTitle: translate('configuration-layers-component.changes-cancel'),
          buttons: [
            {
              title: 'Discard changes',
              color: SimpleDialogButtonColor.orange,
              handler: (dialogRef) => {
                dialogRef.close();
                this.cancelEdit();
                resolve(true);
              },
            },
          ],
        })
        .afterClosed()
        .subscribe(async (result) => {
          if (result) {
            const res = await this.saveEdit();
            resolve(res);
          } else {
            resolve(false);
          }
        });
    } else {
      resolve(true);
    }
  }

  cancelEdit() {
    this.configuration.update((config) => ({
      ...config,
      parents: [...this.originalConfigurationParents],
    }));
    this.configurationLayers.set([...this.originalConfigurationLayers]);
    this.canDeleteLayers = this.configuration().parents.length > 1;

    this.hasChanges$.next(false);
  }

  saveEdit() {
    this.isLoading = true;

    this.alertService.showToast(
      translate('configuration-layers-component.recalculating-message', {
        name: this.configuration().name,
      })
    );

    this.configService.updateConfiguration(this.configuration()).then(
      () => {
        this.hasChanges$.next(false);

        this.alertService.showToast(
          translate('configuration-layers-component.recalculating-success')
        );

        setTimeout(() => {
          if (window.location.hash !== '#settings') {
            window.location.hash = 'settings';
          }
          window.location.reload();
        }, 1500);
      },
      (err) => {
        this.isLoading = false;
        const errorMsg =
          err?.error?.error || translate('configuration-layers-component.unknown-error');

        this.alertService.showErrorToast(
          translate('configuration-layers-component.failed-to-save', { msg: errorMsg })
        );
      }
    );
  }

  get editMode() {
    return this.hasChanges$.value;
  }

  get editConfigurationTooltip() {
    if (!this.selectedLayer) {
      return translate('configuration-layers-component.select-a-layer');
    }
    if (this.selectedLayer.isLanguageTemplate) {
      return translate('configuration-layers-component.cannot-edit-template');
    }
    if (this.selectedLayer.isIndustryPack) {
      return translate('configuration-layers-component.cannot-edit-ip');
    }
  }

  get deleteLayerTooltip() {
    if (!this.canEditLayers) {
      return translate('configuration-layers-component.read-only-cannot-delete');
    }
    if (!this.canDeleteLayers) {
      return translate('configuration-layers-component.cannot-delete-last');
    }
    return translate('configuration-layers-component.delete-layer');
  }
}
